import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { BuildVersionMeta } from '@/utils/api-pb';

interface VersionsPanelProps {
  versions: BuildVersionMeta[];
  currentVersion: number | null;
  isBusy: boolean;
  onRestore: (version: number) => void;
}

function formatDate(value: string, locale: string): string {
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VersionsPanel({ versions, currentVersion, isBusy, onRestore }: VersionsPanelProps) {
  const { t, i18n } = useTranslation('builder');

  if (!versions.length) {
    return (
      <p className="font-segoe text-[14px] text-[color:var(--grey-text,#565858)]">
        {t('versions.empty')}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-[8px]">
      {versions.map((version) => {
        const isCurrent = version.version === currentVersion;

        return (
          <li
            key={version.id ?? version.version}
            className="flex items-center gap-[12px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] px-[12px] py-[10px]"
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="font-manrope text-[14px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
                {t('editor.version', { n: version.version })}
                {isCurrent && (
                  <span className="ml-[8px] font-segoe text-[12px] font-normal text-[color:var(--main-brand,#2197f1)]">
                    {t('versions.current')}
                  </span>
                )}
              </span>
              <span className="truncate font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                {version.change_summary || t(`versions.author.${version.author}`)}
                {' · '}
                {formatDate(version.created, i18n.language)}
              </span>
            </div>

            {/* Restoring appends a new version, so the current state is never lost. */}
            {!isCurrent && (
              <Button
                variant="brand-soft"
                size="brand-sm"
                disabled={isBusy}
                onClick={() => onRestore(version.version)}
              >
                {t('versions.restore')}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
